import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { zip } from 'fflate';
import type IntlService from 'ember-intl/services/intl';
import type StoreService from 'scaleflow/services/store';
import { initializeVdrClient } from 'scaleflow/utils/aws-clients/s3VDRClient';
import {
  RFI_CATEGORY_IDS,
  categoryIdFromKey,
  isMetadataSidecar,
  rfiKey,
  rfiPrefix,
  type RfiCategoryId,
} from 'scaleflow/utils/rfi';

type VdrClient = Awaited<ReturnType<typeof initializeVdrClient>>;

/**
 * Most bytes one "Download all" zip holds. A zip is built in memory (each file
 * read in full, then the archive), so peak use is about twice this.
 */
const ZIP_CHUNK_BYTES = 300 * 1024 * 1024;

/** An uploaded file and its path inside a zip (`{category title}/{name}`). */
type ZipEntry = { file: RfiFile; path: string };

export type RfiFileStatus = 'uploading' | 'uploaded' | 'failed';

export interface RfiFile {
  /** The S3 key, which is unique per category and filename. */
  id: string;
  name: string;
  size: number;
  /** `null` until the upload finishes. */
  lastModified: Date | null;
  status: RfiFileStatus;
  /** Bytes sent so far; equals `size` once uploaded. */
  loaded: number;
}

export interface RfiCategoryState {
  id: RfiCategoryId;
  number: number;
  /** Everything shown for the category, including uploads in flight or failed. */
  files: RfiFile[];
  /** Files that are safely in S3. */
  count: number;
  provided: boolean;
  lastUpdated: Date | null;
}

/**
 * Holds the files for each RFI category, backed by the VDR bucket at
 * `{tenant}/rfi/{category}/{filename}`.
 *
 * S3 is the source of truth: the routes call `load()` to read it, and nothing
 * is stored client-side. Re-uploading a filename overwrites that object.
 */
export default class RfiService extends Service {
  @service declare store: StoreService;
  @service declare organisation: { id: string };
  @service declare intl: IntlService;

  @tracked private uploads: Partial<Record<RfiCategoryId, RfiFile[]>> = {};
  @tracked errorMessage: string | null = null;
  @tracked isDownloadingAll = false;

  private get tenantId(): string {
    return String(this.organisation.id);
  }

  /** Extracted so tests can override it on the instance rather than hit AWS. */
  async getVdrClient(): Promise<VdrClient> {
    return initializeVdrClient(this.store);
  }

  get categories(): RfiCategoryState[] {
    return RFI_CATEGORY_IDS.map((id, index) => {
      const files = this.uploads[id] ?? [];
      const uploaded = files.filter((file) => file.status === 'uploaded');
      const times = uploaded.map((file) => file.lastModified?.getTime() ?? 0);
      return {
        id,
        number: index + 1,
        files,
        count: uploaded.length,
        provided: uploaded.length > 0,
        lastUpdated: times.length ? new Date(Math.max(...times)) : null,
      };
    });
  }

  get totalFiles(): number {
    return this.categories.reduce((sum, category) => sum + category.count, 0);
  }

  /**
   * Reads what is already in the bucket. Uploads still in flight, or failed and
   * not yet dismissed, are kept: re-entering the route calls this again and
   * must not wipe them.
   */
  async load(): Promise<void> {
    try {
      const client = await this.getVdrClient();
      const objects = await client.listObjects(rfiPrefix(this.tenantId));

      const next: Partial<Record<RfiCategoryId, RfiFile[]>> = {};
      for (const object of objects) {
        if (isMetadataSidecar(object.key)) continue;
        const categoryId = categoryIdFromKey(this.tenantId, object.key);
        if (!categoryId) continue;
        (next[categoryId] ??= []).push({
          id: object.key,
          name: object.name,
          size: object.size,
          lastModified: object.lastModified,
          status: 'uploaded',
          loaded: object.size,
        });
      }

      for (const id of RFI_CATEGORY_IDS) {
        const listed = next[id] ?? [];
        listed.sort(
          (a, b) =>
            (a.lastModified?.getTime() ?? 0) - (b.lastModified?.getTime() ?? 0),
        );
        const listedIds = new Set(listed.map((file) => file.id));
        const pending = (this.uploads[id] ?? []).filter(
          (file) => file.status !== 'uploaded' && !listedIds.has(file.id),
        );
        next[id] = [...listed, ...pending];
      }

      this.uploads = next;
      this.errorMessage = null;
    } catch (error) {
      console.error('Failed to load RFI documents', error);
      this.errorMessage = this.intl.t('rfi.load-failed');
    }
  }

  async add(categoryId: RfiCategoryId, files: File[]): Promise<void> {
    this.errorMessage = null;
    const failed: string[] = [];

    try {
      const client = await this.getVdrClient();
      await Promise.all(
        files.map(async (file) => {
          if (!(await this.uploadOne(client, categoryId, file))) {
            failed.push(file.name);
          }
        }),
      );
    } catch (error) {
      console.error('Failed to start RFI upload', error);
      failed.push(...files.map((file) => file.name));
    }

    if (failed.length) {
      this.errorMessage = this.intl.t('rfi.upload-failed-banner', {
        name: failed.join(', '),
      });
    }
  }

  async remove(categoryId: RfiCategoryId, fileId: string): Promise<void> {
    const file = (this.uploads[categoryId] ?? []).find((f) => f.id === fileId);
    if (!file) return;

    // A failed upload never reached S3, so there is nothing to delete there.
    if (file.status === 'uploaded') {
      try {
        const client = await this.getVdrClient();
        await client.deleteObject(file.id);
      } catch (error) {
        console.error(`Failed to delete "${file.name}"`, error);
        this.errorMessage = this.intl.t('rfi.delete-failed', {
          name: file.name,
        });
        return;
      }
    }

    this.uploads = {
      ...this.uploads,
      [categoryId]: (this.uploads[categoryId] ?? []).filter(
        (f) => f.id !== fileId,
      ),
    };
  }

  async download(categoryId: RfiCategoryId, fileId: string): Promise<void> {
    const file = (this.uploads[categoryId] ?? []).find((f) => f.id === fileId);
    if (!file) return;

    try {
      const client = await this.getVdrClient();
      saveBlob(await this.fetchBlob(client, file.id), file.name);
    } catch (error) {
      console.error(`Failed to download "${file.name}"`, error);
      this.errorMessage = this.intl.t('rfi.download-failed', {
        name: file.name,
      });
    }
  }

  /**
   * Downloads every uploaded file, one folder per category (named by its
   * translated title).
   *
   * Zips are built in memory, so files are packed into zips of at most
   * `ZIP_CHUNK_BYTES` each, saved one after another. A file bigger than that
   * on its own is saved directly from S3 instead of zipped.
   * If a file fails, its zip is not saved and the remaining downloads stop;
   * anything already saved stays saved.
   */
  async downloadAll(): Promise<void> {
    if (this.isDownloadingAll) return;
    this.isDownloadingAll = true;
    this.errorMessage = null;

    try {
      const client = await this.getVdrClient();
      const { direct, zips } = planDownloads(this.uploadedFiles);
      const total = direct.length + zips.length;
      let part = 0;

      // Presigning is quick, so these go first, while the click is recent
      // enough for Safari to allow the downloads.
      for (const { file } of direct) {
        const name = downloadName(
          this.tenantId,
          ++part,
          total,
          `-${file.name}`,
        );
        saveUrl(await client.getDownloadUrl(file.id, name), name);
      }

      for (const files of zips) {
        const name = downloadName(this.tenantId, ++part, total, '.zip');
        saveBlob(await this.zipFiles(client, files), name);
      }
    } catch (error) {
      console.error('Failed to download all RFI documents', error);
      this.errorMessage = this.intl.t('rfi.download-all-failed');
    } finally {
      this.isDownloadingAll = false;
    }
  }

  /** Every uploaded file, with where it goes in a zip. */
  private get uploadedFiles(): ZipEntry[] {
    return this.categories.flatMap((category) => {
      const folder = this.intl.t(`rfi.categories.${category.id}.title`);
      return category.files
        .filter((file) => file.status === 'uploaded')
        .map((file) => ({ file, path: `${folder}/${file.name}` }));
    });
  }

  /** Downloads `files` in parallel and zips them into one archive. */
  private async zipFiles(client: VdrClient, files: ZipEntry[]): Promise<Blob> {
    const entries: Record<string, Uint8Array> = {};
    await Promise.all(
      files.map(async ({ file, path }) => {
        const blob = await this.fetchBlob(client, file.id);
        entries[path] = new Uint8Array(await blob.arrayBuffer());
      }),
    );

    const archive = await new Promise<Uint8Array>((resolve, reject) => {
      // Level 0: most RFI files (PDF, XLSX, images) are already compressed.
      zip(entries, { level: 0 }, (error, data) =>
        error ? reject(error) : resolve(data),
      );
    });
    return new Blob([archive as BlobPart], { type: 'application/zip' });
  }

  private async fetchBlob(client: VdrClient, key: string): Promise<Blob> {
    let blob: Blob | undefined;
    for await (const update of client.downloadFileWithProgress(key)) {
      if (update.blob) blob = update.blob;
    }
    if (!blob) throw new Error('Download produced no data');
    return blob;
  }

  /** Resolves to whether the file made it into S3. */
  private async uploadOne(
    client: VdrClient,
    categoryId: RfiCategoryId,
    file: File,
  ): Promise<boolean> {
    const key = rfiKey(this.tenantId, categoryId, file.name);
    this.upsert(categoryId, {
      id: key,
      name: file.name,
      size: file.size,
      lastModified: null,
      status: 'uploading',
      loaded: 0,
    });

    try {
      await client.uploadFileMultipart(
        {
          key,
          body: file,
          contentType: file.type || 'application/octet-stream',
        },
        (loaded) => this.patch(categoryId, key, { loaded }),
      );
      this.patch(categoryId, key, {
        status: 'uploaded',
        loaded: file.size,
        lastModified: new Date(),
      });
      return true;
    } catch (error) {
      console.error(`Failed to upload "${file.name}"`, error);
      this.patch(categoryId, key, { status: 'failed' });
      return false;
    }
  }

  // Both reassign the whole record: elements of a tracked array are not
  // themselves tracked, so mutating one in place would never re-render.
  private upsert(categoryId: RfiCategoryId, entry: RfiFile) {
    const others = (this.uploads[categoryId] ?? []).filter(
      (file) => file.id !== entry.id,
    );
    this.uploads = { ...this.uploads, [categoryId]: [...others, entry] };
  }

  private patch(
    categoryId: RfiCategoryId,
    fileId: string,
    changes: Partial<RfiFile>,
  ) {
    this.uploads = {
      ...this.uploads,
      [categoryId]: (this.uploads[categoryId] ?? []).map((file) =>
        file.id === fileId ? { ...file, ...changes } : file,
      ),
    };
  }
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  saveUrl(url, filename);
  URL.revokeObjectURL(url);
}

function saveUrl(url: string, filename: string) {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Decides how each file will be downloaded:
 * - `direct`: files over `ZIP_CHUNK_BYTES`, each saved on its own, unzipped.
 * - `zips`: every other file, grouped into batches; each batch becomes one zip.
 *   Files are added in order, and a new batch starts when the next file would
 *   push the batch's total size past `ZIP_CHUNK_BYTES`.
 */
function planDownloads(files: ZipEntry[]): {
  direct: ZipEntry[];
  zips: ZipEntry[][];
} {
  const direct = files.filter(({ file }) => file.size > ZIP_CHUNK_BYTES);
  const zips: ZipEntry[][] = [];
  let current: ZipEntry[] = [];
  let total = 0;
  for (const entry of files) {
    if (entry.file.size > ZIP_CHUNK_BYTES) continue;
    if (current.length && total + entry.file.size > ZIP_CHUNK_BYTES) {
      zips.push(current);
      current = [];
      total = 0;
    }
    current.push(entry);
    total += entry.file.size;
  }
  if (current.length) zips.push(current);
  return { direct, zips };
}

/**
 * One numbering across every download (`42-rfi-evidences-2-of-4.zip`),
 * dropped when there is only one.
 */
function downloadName(
  tenantId: string,
  part: number,
  total: number,
  suffix: string,
): string {
  const numbering = total > 1 ? `-${part}-of-${total}` : '';
  return `${tenantId}-rfi-evidences${numbering}${suffix}`;
}
